/* @flow */
import to from 'to-case';
import { apiService, userStore } from 'services/ServiceRegistry';
import { activities } from 'components/Activity/ActivityData';

import { disabledNotificationTypes, makeUserNotificationFields } from './form/fields';
import Form from '../../forms/_.extend';

type EmailNotification = {
  mail_type_id: string,
  mail_key: string,
  subscribed: boolean,
  only_assigned_to_me: boolean,
};

type AppNotification = {
  enable: number,
  event_type_id: string,
  id: string,
  name: string,
  threshold: string,
  user_id: string,
};

const staticThresholds = [7, 14, 28, 45, 60];

const userFields = (itemData: Object, t) => {
  if (!userStore.userLanguages) {
    userStore.getUserLanguages();
  }

  const notificationSettingFields = makeUserNotificationFields(
    {
      email: itemData.email_notifications,
      app: itemData.app_notifications,
    },
    t
  );

  return [
    {
      name: 'userID',
      label: t('general:users.labels.id'),
      fields: [
        {
          name: 'id',
          label: t('general:users.labels.id'),
          rules: 'required',
          value: itemData.id,
        },
      ],
    },
    {
      name: 'disableAccount',
      label: t('general:accounts.labels.status'),
      fields: [
        {
          name: 'disabled',
          label: t('general:actions.disable_account'),
          rules: 'required',
          value: !itemData.is_enabled,
        },
      ],
    },
    {
      name: 'userProfile',
      label: t('general:users.labels.profile'),
      fields: [
        {
          name: 'firstName',
          label: t('general:labels.first_name'),
          value: itemData.first_name,
        },
        {
          name: 'lastName',
          label: t('general:labels.last_name'),
          value: itemData.last_name,
        },
        {
          name: 'email',
          label: t('general:labels.email_address'),
          value: itemData.email,
        },
        {
          name: 'phoneNumber',
          label: t('general:labels.phone_number'),
          value: itemData.tel || 'N/A',
        },
        {
          name: 'stateCounty',
          label: t('general:state_county'),
          value: itemData.state,
        },
        {
          name: 'country',
          label: t('general:country'),
          value: {
            name: itemData.country,
            id: itemData.country_id,
          },
        },
        {
          name: 'company',
          label: t('general:company'),
          value: itemData.company_name,
        },
        {
          name: 'city',
          label: t('general:city'),
          value: itemData && itemData.city ? itemData.city : 'N/A',
        },
        {
          name: 'postcode',
          label: t('general:postcode'),
          value: itemData && itemData.post_code ? itemData.post_code : 'N/A',
        },
        {
          name: 'region',
          label: t('general:region'),
          value: itemData.region.name,
          extra: {
            selectID: itemData.region.id,
          },
        },
      ],
    },
    {
      name: 'displaySettings',
      label: t('general:labels.display_settings'),
      fields: [
        {
          name: 'currency',
          label: t('general:currency'),
          rules: 'required',
          value: itemData.currency,
        },
        {
          name: 'price',
          label: t('general:labels.price_format'),
          rules: 'required',
          value: {
            format: itemData.price_format,
            id: itemData.price_format_id,
          },
        },
      ],
    },
    {
      name: 'notificationSettings',
      label: t('general:labels.notification_settings'),
      fields: notificationSettingFields,
    },
  ];
};

class UserProfileFormEdit extends Form {}

const userEdit = (fields: any[], itemData: Object, store: Object, localState: Object, t) => {
  const hooks = {
    async onSuccess(form: Object) {
      localState.isLoading = true;
      localState.responseMessage = t('general:messages.saving_form');
      localState.showResponseMessage = true;
      localState.hasError = false;
      localState.hasSuccess = true;

      const formValues = form.values();
      const { disabled } = formValues.disableAccount;
      const {
        company,
        country,
        email,
        firstName,
        lastName,
        phoneNumber,
        stateCounty,
      } = formValues.userProfile;

      const { currency, price } = formValues.displaySettings;
      const emailNotifications: EmailNotification[] = [];

      for (const itemEmailNotification of itemData.email_notifications) {
        const notificationType = to.pascal(itemEmailNotification.mail_key);

        if (!disabledNotificationTypes.includes(notificationType)) {
          emailNotifications.push({
            mail_type_id: itemEmailNotification.mail_type_id,
            mail_key: itemEmailNotification.mail_key,
            subscribed:
              formValues.notificationSettings[notificationType].emailOnlyMe ||
              formValues.notificationSettings[notificationType].email,
            only_assigned_to_me: formValues.notificationSettings[notificationType].emailOnlyMe,
          });
        }
      }

      const appNotifications: AppNotification[] = [];

      for (const itemAppNotification of itemData.app_notifications) {
        const type = itemAppNotification.name || to.pascal(itemAppNotification.mail_key);
        let isEnabled = false;
        let enableThreshold = '';
        let disableThreshold = '';

        for (const threshold of staticThresholds) {
          const thresholds = formValues.notificationSettings[type].thresholds;

          if (thresholds[`threshold_${threshold}`]) {
            isEnabled = true;
            enableThreshold += enableThreshold === '' ? threshold : `,${threshold}`;
          } else {
            disableThreshold += disableThreshold === '' ? threshold : `,${threshold}`;
          }
        }

        appNotifications.push({
          enable: isEnabled ? 1 : 0,
          event_type_id: itemAppNotification.event_type_id,
          id: itemAppNotification.id,
          name: type,
          threshold: isEnabled ? enableThreshold : disableThreshold,
          user_id: itemAppNotification.user_id,
        });
      }

      const params = {
        activity_tag: activities.edited.tag, // eslint-disable-line camelcase
        first_name: firstName, // required
        last_name: lastName, // required
        tel: phoneNumber, // required
        country_id: country.id, // required
        state: stateCounty, // required
        company_name: company, // required
        email: email, // eslint-disable-line object-shorthand
        language_id: userStore.activeUser.language_id, // eslint-disable-line object-shorthand
        currency: currency.name, // eslint-disable-line object-shorthand
        currency_id: currency.id, // eslint-disable-line object-shorthand
        price_format: price.format, // eslint-disable-line object-shorthand
        price_format_id: price.id,
        email_notifications: emailNotifications, // eslint-disable-line object-shorthand
        app_notifications: appNotifications, // eslint-disable-line object-shorthand
        is_enabled: !disabled,
        region_id: form.$('userProfile.region').extra.selectID,
      };

      apiService
        .putResource(`users/profile/${itemData.id}`, params)
        .then((response: Object) => {
          store.updateActiveUser(response);
          localState.isLoading = false;
          localState.responseMessage = t('general:messages.save_success');

          setTimeout(() => {
            localState.showResponseMessage = false;
            setTimeout(() => {
              localState.hasSuccess = false;
            }, 225);
          }, 1200);

          return response;
        })
        .catch((err: *) => {
          localState.isLoading = false;
          localState.responseMessage = err.message || t('general:messages.save_error');
          localState.hasSuccess = false;
          localState.hasError = true;
        });
    },
    onError(form: Object) {
      localState.isLoading = false;
      localState.responseMessage = t('general:messages.form_error');
      localState.showResponseMessage = true;
      localState.hasSuccess = false;
      localState.hasError = true;
    },
  };

  return new UserProfileFormEdit({ fields }, { hooks, name: t('general:profile_preferences') });
};

export const getForm = (
  itemData: Object,
  store: Object,
  localState: Object,
  t: Function,
  i18n: Object
) => {
  const fields = userFields(itemData, t);
  return userEdit(fields, itemData, store, localState, t, i18n);
};
